"use client";

import { useEffect, useRef, useState } from "react";
import { NumberTicker } from "@/components/ui/number-ticker";
import { useContent } from "@/components/site/ContentProvider";
import styles from "./story-facts.module.css";

/**
 * Story facts arm on the side-swap milestone (`xattir:story-facts`), not an
 * independent long timer that can lead the center plane.
 */
export function useStoryFactsActive(delayMs = 0) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (active) return;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const arm = () => {
      if (timer) clearTimeout(timer);
      if (delayMs <= 0) {
        setActive(true);
        return;
      }
      timer = setTimeout(() => setActive(true), delayMs);
    };

    window.addEventListener("xattir:story-facts", arm);
    return () => {
      window.removeEventListener("xattir:story-facts", arm);
      if (timer) clearTimeout(timer);
    };
  }, [active, delayMs]);

  return active;
}

/** Count accelerates past 9999, then collapses into ∞. */
export function InfinityRushTicker({
  active,
  className,
}: {
  active: boolean;
  className?: string;
}) {
  const site = useContent();
  const [text, setText] = useState("0");
  const [infinite, setInfinite] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!active || started.current) return;
    started.current = true;

    let value = 0;
    let step = 3;
    let gap = 70;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const END = 14000;

    const tick = () => {
      value += step;
      if (value >= END) {
        setText("∞");
        setInfinite(true);
        return;
      }
      setText(String(Math.floor(value)));
      /* Keep climbing through 4–5 digits before the snap. */
      step = Math.min(step * 1.14, 720);
      gap = Math.max(gap * 0.93, 18);
      timer = setTimeout(tick, gap);
    };

    timer = setTimeout(tick, 160);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [active]);

  return (
    <span
      className={`${styles.rush}${infinite ? ` ${styles.infinite}` : ""}${className ? ` ${className}` : ""}`}
      aria-label={infinite ? site.aria.infinity : text}
    >
      {text}
    </span>
  );
}

export function StoryFactValues({ active }: { active: boolean }) {
  const site = useContent();
  return (
    <>
      {site.about.facts.map((fact, index) => (
        <div className={`facts_block f f-c _ele ${styles.fact}`} key={fact.label}>
          <h5 className={`f a-c ${styles.value}`} dir="ltr">
            {"symbol" in fact && fact.symbol ? (
              <InfinityRushTicker active={active} className="fact_ticker" />
            ) : (
              <>
                <NumberTicker
                  value={fact.value ?? 0}
                  active={active}
                  delay={0.15 * index}
                  className="fact_ticker"
                />
                {fact.suffix ? <span>{fact.suffix}</span> : null}
              </>
            )}
          </h5>
          <span className={`small light_60 ${styles.label}`}>{fact.label}</span>
        </div>
      ))}
    </>
  );
}
